import { useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '../../lib/cn'
import { useFocusTrap } from './useFocusTrap'

interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  className?: string
}

export function Dialog({ open, onClose, title, children, className }: DialogProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDialogElement>(null)

  // Before the showModal effect so the trap captures pre-open focus to restore.
  useFocusTrap(dialogRef, { active: open })

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open && !dialog.open) {
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      aria-labelledby={titleId}
      className={cn(
        // m-auto restores the UA's centered-modal placement, which Tailwind
        // preflight's margin reset removes from native <dialog>. The calc width
        // keeps a 1rem gutter each side on small viewports (never full-bleed);
        // max-w-md still caps the panel on larger screens.
        'm-auto w-[calc(100%-2rem)] max-w-md rounded-card border border-border-warm bg-surface-raised p-0 text-text shadow-warm-lg',
        'backdrop:bg-overlay',
        className,
      )}
    >
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <h2 id={titleId} className="font-display text-xl text-text-strong">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 -mt-1 shrink-0 rounded-control p-1 text-text-muted transition duration-200 ease-out hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            <X className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </dialog>
  )
}
